import { useCallback, useEffect, useRef } from 'react';

import { Box } from '@bonusly/design-system';

import { BIZY_PANEL_MAX_WIDTH, BIZY_PANEL_MIN_WIDTH, useBizySidePanelStore } from './store/use-bizy-side-panel-store';

interface BizySidePanelResizeHandleProps {
	maxWidth?: number;
	minWidth?: number;
	onResizeWidth?: (width: number) => void;
	panelLeftEdge: number;
}

export function BizySidePanelResizeHandle({
	maxWidth = BIZY_PANEL_MAX_WIDTH,
	minWidth = BIZY_PANEL_MIN_WIDTH,
	onResizeWidth,
	panelLeftEdge,
}: BizySidePanelResizeHandleProps) {
	const setWidth = useBizySidePanelStore((state) => state.setWidth);
	const setIsResizing = useBizySidePanelStore((state) => state.setIsResizing);
	const activePointerId = useRef<number | null>(null);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			// Only respond to primary pointer (left click / single touch / primary pen)
			if (!e.isPrimary) return;
			e.preventDefault();

			const handleEl = e.currentTarget;
			// Capture this pointer so all subsequent pointer events come to the
			// handle even if the cursor crosses iframes or other elements.
			handleEl.setPointerCapture(e.pointerId);
			activePointerId.current = e.pointerId;

			setIsResizing(true);
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';

			const handlePointerMove = (moveEvent: PointerEvent) => {
				if (moveEvent.pointerId !== activePointerId.current) return;
				const newWidth = moveEvent.clientX - panelLeftEdge;
				const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
				if (onResizeWidth) onResizeWidth(clampedWidth);
				else setWidth(clampedWidth);
			};

			const stopDrag = () => {
				if (activePointerId.current !== null && handleEl.hasPointerCapture(activePointerId.current)) {
					handleEl.releasePointerCapture(activePointerId.current);
				}
				activePointerId.current = null;
				setIsResizing(false);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
				handleEl.removeEventListener('pointermove', handlePointerMove);
				handleEl.removeEventListener('pointerup', stopDrag);
				handleEl.removeEventListener('pointercancel', stopDrag);
				handleEl.removeEventListener('lostpointercapture', stopDrag);
			};

			handleEl.addEventListener('pointermove', handlePointerMove);
			handleEl.addEventListener('pointerup', stopDrag);
			handleEl.addEventListener('pointercancel', stopDrag);
			handleEl.addEventListener('lostpointercapture', stopDrag);
		},
		[maxWidth, minWidth, onResizeWidth, panelLeftEdge, setWidth, setIsResizing]
	);

	useEffect(() => {
		return () => {
			activePointerId.current = null;
			setIsResizing(false);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [setIsResizing]);

	return (
		<Box
			position="absolute"
			right={0}
			top={0}
			bottom={0}
			w="6px"
			cursor="col-resize"
			zIndex={1}
			onPointerDown={handlePointerDown}
			sx={{ touchAction: 'none' }}
			_hover={{ '& > div': { opacity: 1 } }}
			role="separator"
			aria-orientation="vertical"
			aria-label="Resize Bizy panel"
		>
			<Box
				position="absolute"
				right="2px"
				top="50%"
				transform="translateY(-50%)"
				w="3px"
				h="32px"
				borderRadius="full"
				bg="text.softest"
				opacity={0}
				transition="opacity 0.15s"
			/>
		</Box>
	);
}
